import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import firebase from "firebase/compat/app";
import "firebase/compat/database";
import "./styles/ThankyouPage.css"; // CSS file for styling
import WebsiteFooter from "../components/WebsiteFooter";

const ThankYouPage = ({ loggedInUser, setCart }) => {
  const { invoiceNo } = useParams();
  const [orderDetails, setOrderDetails] = useState(null);
  const [orderConfirmationDetails, setOrderConfirmationDetails] = useState(null);

  // Calculate the item price with GST
  const calculateItemPriceWithGST = (itemPrice) => {
    const basePrice = itemPrice / 1.12;  // 12% GST included in the price (6% CGST and 6% SGST)
    const tax = basePrice * 0.12;  // Total tax (CGST + SGST)
    return {
      basePrice: basePrice.toFixed(2),
      tax: tax.toFixed(2),
    };
  };

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        if (!loggedInUser || !invoiceNo) {
          console.error("User not logged in or missing invoice number");
          return;
        }

        // Fetch OrderConfirmationDetails to get the order_id
        const orderConfirmationRef = firebase
          .database()
          .ref(`Customers/${loggedInUser}/Orders/${invoiceNo}/OrderConfirmationDetails`);

        const confirmationSnapshot = await orderConfirmationRef.once("value");
        if (confirmationSnapshot.exists()) {
          setOrderConfirmationDetails(confirmationSnapshot.val());
        } else {
          console.error("No order confirmation details found.");
        }

        // Fetch OrderDetails for the items
        const customerOrdersRef = firebase
          .database()
          .ref(`Customers/${loggedInUser}/Orders/${invoiceNo}/OrderDetails`);

        const snapshot = await customerOrdersRef.once("value");
        setCart([]); // Clear the cart on page load
        if (snapshot.exists()) {
          setOrderDetails(snapshot.val());
        } else {
          console.error("No order details found.");
        }
      } catch (error) {
        console.error("Error fetching order details:", error);
      }
    };

    fetchOrderDetails();
  }, [invoiceNo, loggedInUser, setCart]);

  const calculateTotalTax = () => {
    if (orderDetails) {
      const itemTotal = orderDetails.order_items.reduce((total, item) => total + (item.selling_price * item.units), 0);
      const shipping = orderDetails.shipping_charges || 0;

      // Assuming CGST and SGST are 6% each
      const cgst = (itemTotal * 0.06);
      const sgst = (itemTotal * 0.06);
      const totalWithGST = (itemTotal + cgst + sgst + shipping).toFixed(2); // Ensure total is a number

      return {
        basePrice: itemTotal.toFixed(2),
        cgst: cgst.toFixed(2),
        sgst: sgst.toFixed(2),
        totalWithGST: totalWithGST,
      };
    }
    return null;
  };

  const taxBreakdown = calculateTotalTax();

  // Calculate subtotal
  const subtotal = orderDetails?.order_items.reduce((total, item) => {
    const { basePrice } = calculateItemPriceWithGST(item.selling_price);
    return total + (basePrice * item.units);
  }, 0);

  const totalTax = subtotal * 0.12;  // Total tax (CGST + SGST)
  const shippingCharges = orderDetails?.shipping_charges || 0;
  const total = subtotal + totalTax + shippingCharges;

  return (
    <>
      <div className="thank-you-container">
        <h1 className="thank-you-header">Thank you!</h1>
        <p className="thank-you-subtext">Your order has been received.</p>

        <div className="order-details">
          {orderConfirmationDetails && (
            <>
              <p><strong>Order ID:</strong> {orderConfirmationDetails.order_id}</p>
            </>
          )}
        </div>

        <div className="product-cards">
          <h2>Order Summary</h2>
          {orderDetails && orderDetails.order_items && orderDetails.order_items.length > 0 ? (
            <div className="order-items-list">
              <div className="order-item-header">
                <div className="item-name">Item Name</div>
                <div className="item-qty">Quantity</div>
                <div className="item-price">Price</div>
              </div>
              {orderDetails.order_items.map((item, index) => {
                const { basePrice } = calculateItemPriceWithGST(item.selling_price);
                return (
                  <div key={index} className="order-item-row">
                    <div className="item-name">{item.name}</div>
                    <div className="item-qty">
                      <strong>  {item.units}</strong>
                    </div>
                    <div className="item-price">
                      <strong>Rs. {basePrice}</strong>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p>No items found in the order.</p>
          )}

          {orderDetails && taxBreakdown && (
            <>
              <div className="sub-total item-row">
                <span>Subtotal:</span>
                <strong>Rs. {subtotal.toFixed(2)}</strong>
              </div>

              <div className="order-total item-row">
                <span>Tax (12%):</span>
                <strong>Rs. {totalTax.toFixed(2)}</strong>
              </div>

              <div className="order-total item-row">
                <span>Shipping Charges:</span>
                <strong>Rs. {shippingCharges}</strong>
              </div>

              <div className="order-total item-row">
                <span>Total:</span>
                <strong>Rs. {total.toFixed(2)}</strong>
              </div>
            </>
          )}
        </div>
      </div>
      <WebsiteFooter />
    </>
  );
};

export default ThankYouPage;
